import { useRef, useState } from "react";
import { FileUp, ImagePlus } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useAvatar } from "@/hooks/useAvatar";
import {
  importOwnAvatar,
  MetaPersonUploadError,
  UPLOAD_MAX_GLB_BYTES,
  type UploadErrorCode,
} from "@/lib/metaperson/metapersonUpload";

// Botão 2 — "Já tenho meu avatar" (task 96bc7d74): envia o .glb que o
// usuário exportou do site da MetaPerson. A validação REAL (rig, visemes) é
// server-side na metaperson-import; cada recusa volta com código próprio e
// vira mensagem específica aqui — a pessoa precisa saber que basta
// re-exportar marcando uma caixa (⛔ mensagem genérica não serve).

function errorKeyFor(code: UploadErrorCode): string {
  switch (code) {
    case "invalid_glb":
      return "metaperson.upload.error.invalid_glb";
    case "upload_too_large":
      return "metaperson.upload.error.upload_too_large";
    case "upload_not_found":
      return "metaperson.upload.error.upload_not_found";
    case "skeleton_missing":
      return "metaperson.upload.error.skeleton_missing";
    case "avaturn_visemes":
      return "metaperson.upload.error.avaturn_visemes";
    case "visemes_missing":
      return "metaperson.upload.error.visemes_missing";
    case "visemes_incomplete":
      return "metaperson.upload.error.visemes_incomplete";
    default:
      return "metaperson.upload.error.unknown";
  }
}

function mbLabel(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

interface MetaPersonImportUploadProps {
  onComplete: () => void;
  onBack: () => void;
}

export default function MetaPersonImportUpload({
  onComplete,
  onBack,
}: MetaPersonImportUploadProps) {
  const { t } = useLanguage();
  const { sex } = useAvatar();
  const [gender, setGender] = useState<"male" | "female">(
    sex === "female" ? "female" : "male",
  );
  const [glbFile, setGlbFile] = useState<File | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState<UploadErrorCode | null>(null);
  const glbInputRef = useRef<HTMLInputElement>(null);
  const posterInputRef = useRef<HTMLInputElement>(null);

  const glbTooLarge = !!glbFile && glbFile.size > UPLOAD_MAX_GLB_BYTES;

  const submit = async () => {
    if (!glbFile || busy || glbTooLarge) return;
    setBusy(true);
    setErrorCode(null);
    try {
      await importOwnAvatar({ glbFile, posterFile, gender });
      onComplete();
    } catch (err) {
      // importOwnAvatar já logou e capturou os "unknown" no Sentry — aqui é
      // só a trilha de UI + a mensagem específica do código.
      console.error("[MetaPersonImportUpload] import failed:", err);
      setErrorCode(err instanceof MetaPersonUploadError ? err.code : "unknown");
      setBusy(false);
    }
  };

  if (busy) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
        <div className="w-7 h-7 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        <p className="text-[13px] font-display text-muted-foreground">
          {t("metaperson.upload.uploading")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {errorCode && (
        <div
          className="rounded-xl p-3.5 text-[12px] leading-relaxed"
          style={{
            background: "rgba(248,113,113,0.08)",
            border: "0.5px solid rgba(248,113,113,0.35)",
            color: "rgba(254,226,226,0.95)",
          }}
        >
          {t(errorKeyFor(errorCode))}
        </div>
      )}

      {/* Arquivo .glb (obrigatório) */}
      <input
        ref={glbInputRef}
        type="file"
        accept=".glb,model/gltf-binary"
        className="hidden"
        onChange={(e) => {
          setGlbFile(e.target.files?.[0] ?? null);
          setErrorCode(null);
        }}
      />
      <button
        type="button"
        onClick={() => glbInputRef.current?.click()}
        className="w-full rounded-2xl p-4 flex items-center gap-3 transition-all active:scale-[0.98] text-left"
        style={{
          background: "rgba(255,255,255,0.03)",
          border:
            "1px dashed " +
            (glbFile ? "hsl(187 72% 50% / 0.5)" : "rgba(255,255,255,0.18)"),
        }}
      >
        <FileUp className="w-5 h-5 shrink-0 text-primary" />
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-display text-foreground/90 truncate">
            {glbFile ? glbFile.name : t("metaperson.upload.pickGlb")}
          </span>
          {glbFile && (
            <span
              className="block text-[11px] mt-0.5"
              style={{
                color: glbTooLarge
                  ? "rgba(248,113,113,0.9)"
                  : "rgba(255,255,255,0.45)",
              }}
            >
              {mbLabel(glbFile.size)}
            </span>
          )}
        </span>
      </button>
      {glbTooLarge && (
        <p className="text-[11px] -mt-2" style={{ color: "rgba(248,113,113,0.9)" }}>
          {t("metaperson.upload.error.upload_too_large")}
        </p>
      )}

      {/* Poster .png (opcional — ausência NÃO bloqueia o import) */}
      <input
        ref={posterInputRef}
        type="file"
        accept=".png,image/png"
        className="hidden"
        onChange={(e) => setPosterFile(e.target.files?.[0] ?? null)}
      />
      <button
        type="button"
        onClick={() => posterInputRef.current?.click()}
        className="w-full rounded-2xl p-3.5 flex items-center gap-3 transition-all active:scale-[0.98] text-left"
        style={{
          background: "rgba(255,255,255,0.02)",
          border: "0.5px solid rgba(255,255,255,0.08)",
        }}
      >
        <ImagePlus className="w-4 h-4 shrink-0 text-foreground/50" />
        <span className="flex-1 min-w-0 text-[12px] text-foreground/70 truncate">
          {posterFile ? posterFile.name : t("metaperson.upload.posterLabel")}
        </span>
      </button>
      <p className="text-[11px] text-muted-foreground/60 -mt-2">
        {t("metaperson.upload.posterHint")}
      </p>

      {/* Corpo do avatar — decide o clipe de idle injetado no servidor */}
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-display text-foreground/75">
          {t("metaperson.upload.genderLabel")}
        </span>
        <div className="flex gap-1.5">
          {(["male", "female"] as const).map((g) => {
            const active = gender === g;
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGender(g)}
                className="px-3 py-1.5 rounded-lg font-display text-[11px] tracking-wide transition-all active:scale-95"
                style={{
                  background: active
                    ? "rgba(34,211,238,0.15)"
                    : "rgba(255,255,255,0.04)",
                  border:
                    "0.5px solid " +
                    (active ? "rgba(34,211,238,0.40)" : "rgba(255,255,255,0.08)"),
                  color: active
                    ? "rgba(236,254,255,0.95)"
                    : "rgba(255,255,255,0.55)",
                }}
              >
                {g === "male"
                  ? t("prerendered.picker.filter_male")
                  : t("prerendered.picker.filter_female")}
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        disabled={!glbFile || glbTooLarge}
        onClick={submit}
        className="w-full h-12 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.98] disabled:opacity-40"
        style={{
          background:
            glbFile && !glbTooLarge
              ? "linear-gradient(135deg, rgba(34,211,238,0.40), rgba(34,211,238,0.25))"
              : "rgba(255,255,255,0.04)",
          border: "0.5px solid rgba(34,211,238,0.55)",
          color: "rgba(236,254,255,0.98)",
        }}
      >
        {t("metaperson.upload.submitCta")}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="text-center text-xs text-muted-foreground/50 py-2"
      >
        {t("common.back")}
      </button>
    </div>
  );
}
